import { useCallback } from 'react';

import { notEmpty } from 'utils/typescript.utils';

import { useStorageState } from 'hooks/useStorageState';
import {
  type LocalCartItem,
  type TicketsCartQueryData,
  useTicketsCart,
  useTicketsCreateCart,
  useTicketsReplaceCart,
} from 'restful/tickets';
import { RestApiError } from 'services/api/restful/errors';
import { TicketsPushAppField } from 'types/api/dato-extensions.types';

interface CreateOrUpdateCartVariables {
  items: Array<LocalCartItem>;
  holdToken?: string;
}

interface CreateOrReplaceCartOptions {
  onSuccess?: (data: TicketsCartQueryData) => void;
  onError?: (error: RestApiError) => void;
}

export const useCreateOrReplaceCart = () => {
  const { createCart, isLoading: createCartLoading } = useTicketsCreateCart();
  const { replaceCart, isLoading: replaceCartLoading } =
    useTicketsReplaceCart();
  const { cart } = useTicketsCart();

  const handleCreateCartOrReplaceCart = useCallback(
    (
      { items, holdToken }: CreateOrUpdateCartVariables,
      options?: CreateOrReplaceCartOptions,
    ) => {
      const mappedItems = items.map((item) => ({
        catalogueItemId: item.catalogueItemId,
        quantity: item.quantity,
        bookObjects: item.bookObjectsInfo
          ?.filter(notEmpty)
          .map((bookObject) => bookObject.label),
      }));
      // Only replace the cart if we have matching hold tokens
      if (!!cart && cart.seatedHoldToken?.token === holdToken) {
        return replaceCart({ items: mappedItems }, options);
      } else {
        return createCart({ items: mappedItems, holdToken }, options);
      }
    },
    [cart, createCart, replaceCart],
  );

  return {
    createOrReplaceCart: handleCreateCartOrReplaceCart,
    isLoading: createCartLoading || replaceCartLoading,
  };
};

export const useAppPushState = (
  eventKey: string,
  ticketsPushApp: TicketsPushAppField,
  hasTicketOffers: boolean,
) => {
  const [appPushSoftIgnoredString, setAppPushSoftIgnored] = useStorageState(
    `nf-app-push-soft-ignored-${eventKey}`,
    {
      defaultValue: 'false',
      engine: 'sessionStorage',
    },
  );
  const showAppPushDialog =
    ticketsPushApp === 'hard' ||
    (ticketsPushApp === 'soft' && appPushSoftIgnoredString !== 'true');

  return {
    showAppPushDialog: hasTicketOffers ? false : showAppPushDialog,
    setAppPushSoftIgnored,
  };
};
